import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import type { RefObject } from 'react';

import { ErrorScreen } from './ErrorScreen';
import { LoginPage } from './LoginPage';
import { StorageAccessPage } from './StorageAccessPage';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { type UrlProps, useUrl } from './useUrl';

import { useValidationState, Subjects, LoadingStatus } from './useValidationState';
import { type NavigationPolicy } from '../navigation-policy';
import { ANALYTICS_CHANNEL } from '../analytics';

export type ChildrenProps = {
	handleContentLoadStarted: () => void;
	handleIframeOnLoad: () => void;
	handleIframeUnload: () => void;
	iframeLoaded?: boolean;
	iframeRef: RefObject<HTMLIFrameElement>;
	isLoading?: boolean;
	navigationPolicy?: NavigationPolicy;
	redirectedToLogin?: boolean;
};

export type Props = UrlProps & {
	children: (props: ChildrenProps) => React.ReactElement;
	navigationPolicy?: NavigationPolicy;
};

type CrossDomainErrorValidationProps = Pick<
	Props,
	'allowedFeatures' | 'children' | 'contentId' | 'navigationPolicy' | 'parentProduct' | 'spaceKey'
> & {
	embeddedConfluenceUrl: ReturnType<typeof useUrl>['embeddedConfluenceUrl'];
	standaloneConfluenceUrl: ReturnType<typeof useUrl>['standaloneConfluenceUrl'];
};

const noop = () => {};

const IFRAME_LOAD_TIMEOUT_MS = 2000;

export const PageLoadValidation = (
	props: Props,
): string | number | boolean | Iterable<React.ReactNode> | React.JSX.Element | null | undefined => {
	const { standaloneConfluenceUrl, embeddedConfluenceUrl } = useUrl(props);
	const {
		state: { contentLoadStarted, iframeLoaded },
	} = useValidationState();

	const isEmbeddedUrlOnSameDomain = embeddedConfluenceUrl
		? window.location.origin === new URL(embeddedConfluenceUrl).origin
		: false;

	const unusedIframeRef = useRef<HTMLIFrameElement>(null);

	return isEmbeddedUrlOnSameDomain ? (
		props.children({
			isLoading: (contentLoadStarted && !iframeLoaded) || false, // if same domain, we bypass loading screen in favor of internal iframe loading experience
			iframeLoaded: iframeLoaded || true, // if same domain, set to true so iframe can handle loading experience
			iframeRef: unusedIframeRef,
			handleIframeOnLoad: noop,
			handleContentLoadStarted: noop,
			handleIframeUnload: noop,
			navigationPolicy: props.navigationPolicy,
		})
	) : (
		<CrossDomainErrorValidation
			key={embeddedConfluenceUrl || props.contentId}
			standaloneConfluenceUrl={standaloneConfluenceUrl}
			embeddedConfluenceUrl={embeddedConfluenceUrl}
			allowedFeatures={props.allowedFeatures}
			{...props}
		/>
	);
};

const CrossDomainErrorValidation = ({
	allowedFeatures,
	children,
	contentId,
	embeddedConfluenceUrl,
	navigationPolicy,
	parentProduct,
	spaceKey,
	standaloneConfluenceUrl,
}: CrossDomainErrorValidationProps) => {
	const { state, dispatch } = useValidationState();

	const {
		iframeLoaded,
		contentLoadStarted,
		shouldAskStorageAccess,
		shouldAskToLogin,
		redirectedToLogin,
		iframeUnloaded,
	} = state;

	const iframeRef = useRef<HTMLIFrameElement>(null);

	const useNewDelayedDispatch = fg('embedded_confluence_delayed_ref_platform');

	/* New implementation refs */
	const iframeOnLoadFiredRef = useRef(false);
	const iframeOnLoadSrcRef = useRef<string | null>(null);
	const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);

	/* Old implementation refs */
	const iframeLoadedWithDelayRef = useRef(false);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isLoginFlowDisabled = useMemo(
		() => allowedFeatures?.includes('disable-login-flow') ?? false,
		[allowedFeatures],
	);

	const reloadEP = useCallback(() => {
		dispatch({
			subject: Subjects.CONTENT_LOAD,
			status: LoadingStatus.RELOAD_EP,
		});
	}, [dispatch]);

	const failToLoad = useMemo(
		() =>
			(iframeLoaded && !contentLoadStarted) ||
			/*
      If anonymous access is permitted globally but restricted on a content's parent space,
      the iframe will attempt a login redirect which we intercept.
      To avoid an error in the iframe, we need to wait for the iframe window to unload
      before unmouting the Iframe component.
    */
			(iframeUnloaded && redirectedToLogin),
		[contentLoadStarted, iframeLoaded, iframeUnloaded, redirectedToLogin],
	);

	const pageNavigationPolicy = useMemo(
		(): NavigationPolicy => ({
			navigate(url, modifiers, defaultNavigate) {
				// modifiy the navigationPolicy to intercept login redirects
				// so that login can be handled outside of the iframe
				if (url.includes('/login?application=confluence')) {
					dispatch({
						subject: Subjects.CONTENT_LOAD,
						status: LoadingStatus.REDIRECTED_TO_LOGIN,
					});
					return;
				}
				return navigationPolicy?.navigate
					? navigationPolicy.navigate(url, modifiers, defaultNavigate)
					: defaultNavigate(url, modifiers);
			},
			shimUrl: navigationPolicy?.shimUrl,
			domainNamesToReplace: navigationPolicy?.domainNamesToReplace,
			spaceKeyFilter: navigationPolicy?.spaceKeyFilter,
		}),
		[navigationPolicy, dispatch],
	);

	// NEW IMPLEMENTATION: Event-driven delayed dispatching
	const handleIframeOnLoadNew = useCallback(() => {
		/* Record that the iframe onLoad event has fired and store the current src.
		   We verify the iframe src to ensure we only handle the correct iframe,
		   as the same Iframe component is also used by the StorageAccessPage component,
		   and the src may change during transitions between view/edit modes or other redirects.
		*/
		if (!iframeRef.current) {
			return;
		}

		iframeOnLoadFiredRef.current = true;
		iframeOnLoadSrcRef.current = iframeRef.current.src;

		// If content has already started loading, dispatch immediately
		if (contentLoadStarted && !iframeLoaded) {
			dispatch({
				subject: Subjects.CONTENT_LOAD,
				status: LoadingStatus.IFRAME_LOADED,
			});
			return;
		}

		// If content hasn't started loading yet, set a timeout to dispatch after a delay
		// This handles the case where content fails to load or never sends the start message
		if (!contentLoadStarted && !iframeLoaded) {
			timeoutIdRef.current = setTimeout(() => {
				// Verify the iframe hasn't changed before dispatching
				if (
					iframeOnLoadFiredRef.current &&
					iframeRef.current?.src === iframeOnLoadSrcRef.current &&
					!iframeLoaded
				) {
					dispatch({
						subject: Subjects.CONTENT_LOAD,
						status: LoadingStatus.IFRAME_LOADED,
					});
				}
			}, IFRAME_LOAD_TIMEOUT_MS);
		}
	}, [contentLoadStarted, iframeLoaded, dispatch]);

	// OLD IMPLEMENTATION: Retry-based delayed dispatching
	const handleIframeOnLoadOld = useCallback(() => {
		const dispatchNow = () => {
			dispatch({
				subject: Subjects.CONTENT_LOAD,
				status: LoadingStatus.IFRAME_LOADED,
			});
		};

		const totalNumberOfTries = 2;
		const iframeOfDispatch = iframeRef.current;
		const iframeSrcOfDispatch = iframeOfDispatch?.src;
		let tries = 0;

		const dispatchWithDelay = () => {
			if (iframeRef.current !== iframeOfDispatch || iframeOfDispatch?.src !== iframeSrcOfDispatch) {
				return;
			}

			++tries;

			if (contentLoadStarted || tries > totalNumberOfTries) {
				if (contentLoadStarted && iframeLoadedWithDelayRef.current) {
					createAnalyticsEvent({
						action: 'delayedDispatch',
						actionSubject: 'iframeOnLoad',
						actionSubjectId: 'crossDomainErrorValidation',
						source: 'pageLoadValidation',
						eventType: 'track',
					}).fire(ANALYTICS_CHANNEL);

					iframeLoadedWithDelayRef.current = false;
				}
				dispatchNow();
			} else {
				if (!iframeLoadedWithDelayRef.current) {
					iframeLoadedWithDelayRef.current = true;
				}
				setTimeout(dispatchWithDelay, 1000);
			}
		};

		dispatchWithDelay();
	}, [contentLoadStarted, dispatch, createAnalyticsEvent]);

	const handleIframeOnLoad = useNewDelayedDispatch ? handleIframeOnLoadNew : handleIframeOnLoadOld;

	// NEW IMPLEMENTATION ONLY: As soon as both the iframe onLoad has fired AND content has started loading, dispatch the IFRAME_LOADED action.
	useEffect(() => {
		if (!useNewDelayedDispatch) {
			return;
		}

		// Only dispatch if both conditions are met and the iframe src hasn't changed
		const shouldDispatch =
			iframeOnLoadFiredRef.current &&
			contentLoadStarted &&
			iframeRef.current?.src === iframeOnLoadSrcRef.current &&
			!iframeLoaded;

		if (shouldDispatch) {
			// Clear any pending timeout since we're dispatching now
			if (timeoutIdRef.current) {
				clearTimeout(timeoutIdRef.current);
				timeoutIdRef.current = null;
			}

			dispatch({
				subject: Subjects.CONTENT_LOAD,
				status: LoadingStatus.IFRAME_LOADED,
			});
		}
	}, [useNewDelayedDispatch, contentLoadStarted, iframeLoaded, dispatch]);

	// NEW IMPLEMENTATION ONLY: Reset the iframe onLoad tracking when content load state is reset (e.g., during reload)
	useEffect(() => {
		if (!useNewDelayedDispatch) {
			return;
		}

		if (!contentLoadStarted && !iframeLoaded) {
			iframeOnLoadFiredRef.current = false;
			iframeOnLoadSrcRef.current = null;

			// Clear any pending timeout
			if (timeoutIdRef.current) {
				clearTimeout(timeoutIdRef.current);
				timeoutIdRef.current = null;
			}
		}
	}, [useNewDelayedDispatch, contentLoadStarted, iframeLoaded]);

	const handleContentLoadStarted = useCallback(() => {
		dispatch({
			subject: Subjects.CONTENT_LOAD,
			status: LoadingStatus.CONTENT_LOAD_STARTED,
		});
	}, [dispatch]);

	const handleIframeUnload = useCallback(() => {
		dispatch({
			subject: Subjects.CONTENT_LOAD,
			status: LoadingStatus.IFRAME_UNLOADED,
		});
	}, [dispatch]);

	if (!failToLoad) {
		return children({
			iframeRef,
			isLoading: contentLoadStarted && !iframeLoaded,
			iframeLoaded,
			handleIframeOnLoad,
			handleContentLoadStarted,
			handleIframeUnload,
			navigationPolicy: pageNavigationPolicy,
			redirectedToLogin,
		});
	}

	if (!isLoginFlowDisabled) {
		if (shouldAskStorageAccess) {
			return (
				<StorageAccessPage
					url={embeddedConfluenceUrl}
					contentId={contentId}
					spaceKey={spaceKey}
					parentProduct={parentProduct}
				/>
			);
		}
		if (shouldAskToLogin) {
			return (
				<LoginPage
					contentId={contentId}
					parentProduct={parentProduct}
					reloadEP={reloadEP}
					spaceKey={spaceKey}
					url={embeddedConfluenceUrl}
				/>
			);
		}
	}
	return <ErrorScreen standaloneConfluenceUrl={standaloneConfluenceUrl} />;
};
